import {
    Paper, Button, TextField, Typography, Fade, Dialog, DialogTitle, DialogActions, DialogContent, TextareaAutosize, Table, TableBody, TableRow, TableCell
} from "@mui/material";
import { Theme, createStyles } from '@mui/material/styles';
import type { WithStyles } from "@mui/styles";
import { withStyles } from "@mui/styles";
import * as React from 'react';
import { connect } from "react-redux";
import SharedMaterialReactTable, { ISharedTableColumn } from "../Shared/MaterialReactTable";
import idssStyle from "../../Configuration/SharedStyling";
import { IGenericUserProps, genericMapStateToProps } from "../../DataClasses/GenericUser";
import { AjaxError } from "rxjs/ajax";
import { firstValueFrom } from "rxjs";
import { IdssLog } from '../../DataClasses/DbData';
import DbService from '../Services/DbService';
import { Redirect } from "react-router-dom";
import { FileCopy as FileCopyIcon } from '@mui/icons-material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import moment from "moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import Select from "react-select";

const styles = (theme: Theme) => createStyles({ ...idssStyle(theme) });

interface ILogViewState {
    data: any[],
    isLoading: boolean,
    userNameFilter: string,
    subIdFilter: string,
    orgIdFilter: string,
    logLevelFilter: any,
    messageFilter: string,
    fromDateFilter: string,
    toDateFilter: string,
    redirect: boolean,
    openLogDialog: boolean,
    selectedLogId: number,
    selectedLogDate: string,
    selectedLogMessage: string,
    selectedLogException: string,
    first: number,
    rows: number,
    totalRecords: number
}

const eventLevels = ['All', 'Fatal', 'Error', 'Warning', 'Information', 'Trace', 'Debug'];

type IStylesProps = WithStyles<typeof styles>;

// VIEW
class LogsView extends React.Component<IGenericUserProps & IStylesProps, ILogViewState> {

    constructor(props: any) {
        super(props);
        var weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        this.state = {
            data: [],
            isLoading: true,
            userNameFilter: "",
            subIdFilter: "",
            orgIdFilter: "",
            logLevelFilter: "",
            messageFilter: "",
            fromDateFilter: weekAgo.toLocaleString(),
            toDateFilter: (new Date()).toLocaleString(),
            redirect: false,
            openLogDialog: false,
            selectedLogDate: "",
            selectedLogMessage: "",
            selectedLogException: "",
            selectedLogId: -1,
            first: 0,
            rows: 100,
            totalRecords: 0
        };
    }

    public componentDidMount() {
        this.refreshModel();
    }

    private redirectToErrorPage() {
        return <Redirect to='/errorpage' />;
    }

    private setRedirectState() {
        this.setState({ redirect: true });
    }

    private onRowClick = (event: any) => {
        const logId = parseInt(event.data.id);
        this.getSelectedLogDetails(logId);
    };

    private getSelectedLogDetails(logId: number) {
        var item = this.state.data.find((x) => x.id === logId);
        if (item) {
            this.setState({
                openLogDialog: true,
                selectedLogId: logId,
                selectedLogDate: item.timeStamp ?? "",
                selectedLogMessage: item.message ?? "",
                selectedLogException: item.exception ?? "No exception details available to show"
            });
        }
        else {
            this.setState({
                openLogDialog: false,
                selectedLogId: -1,
                selectedLogDate: "",
                selectedLogMessage: "",
                selectedLogException: ""
            });
        }
    }

    private getLogDetails(mode: string) {
        var currentLogId = this.state.selectedLogId;
        var id = 0;
        if (currentLogId > 0) {
            var rowIndex = this.state.data.findIndex((x) => x.id === currentLogId);
            if (rowIndex > -1) {
                if (mode === "previous") {
                    rowIndex--;
                }
                else {
                    rowIndex++;
                }
                if (rowIndex < 0 || !this.state.data[rowIndex]) {
                    rowIndex = 0;
                }
                id = this.state.data[rowIndex].id ?? null;
                if (id) {
                    this.getSelectedLogDetails(id);
                }
            }
        }
    }

    public render() {
        if (this.state.redirect) {
            return this.redirectToErrorPage();
        }
        const { classes } = this.props;
        const levelOption = eventLevels.map(m => ({
            label: m,
            value: m
        }));

        return (
            <div>
                <Paper className={classes.root} elevation={3} square={true}>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell align="left">
                                    <Typography variant="h5">
                                        Filters
                                    </Typography>
                                </TableCell>
                                <TableCell align="left" >
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            format="yyyy-MM-dd"
                                            label="From Date"
                                            value={this.state.fromDateFilter ? new Date(this.state.fromDateFilter) : null}
                                            onChange={(newValue) => this.handleFromDateChange(newValue)}
                                            slotProps={{
                                                textField: {
                                                    margin: "normal",
                                                    id: "from-date-picker-inline"
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </TableCell>
                                <TableCell align="left" >
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            format="yyyy-MM-dd"
                                            label="To Date"
                                            value={this.state.toDateFilter ? new Date(this.state.toDateFilter) : null}
                                            onChange={(newValue) => this.handleToDateChange(newValue)}
                                            slotProps={{
                                                textField: {
                                                    margin: "normal",
                                                    id: "to-date-picker-inline"
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </TableCell>
                                <TableCell align="left">
                                    <Select
                                        options={levelOption}
                                        placeholder="Level"
                                        isMulti={false}
                                        value={this.state.logLevelFilter}
                                        closeMenuOnSelect={true}
                                        isSearchable={true}
                                        onChange={this.selectedLevelChange.bind(this)}
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </TableCell>
                                <TableCell align="center" rowSpan={2}>
                                    <Button variant="contained"
                                        className={classes.button}
                                        onClick={() => this.refreshModel()}
                                    >
                                        Filter
                                    </Button>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="left">
                                    <TextField id="filter-name"
                                        label="User Name"
                                        className={classes.textField}
                                        value={this.state.userNameFilter}
                                        onChange={e => this.setState({ userNameFilter: e.target.value })}
                                        onBlur={e => this.setState({ userNameFilter: e.target.value })}
                                        margin="normal"
                                    />
                                </TableCell>
                                <TableCell align="left">
                                    <TextField id="filter-subId"
                                        label="Submission Id"
                                        className={classes.textField}
                                        value={this.state.subIdFilter}
                                        onChange={e => this.setState({ subIdFilter: e.target.value })}
                                        onBlur={e => this.setState({ subIdFilter: e.target.value })}
                                        margin="normal"
                                    />
                                </TableCell>
                                <TableCell align="left">
                                    <TextField id="filter-orgId"
                                        label="Organization Id"
                                        className={classes.textField}
                                        value={this.state.orgIdFilter}
                                        onChange={e => this.setState({ orgIdFilter: e.target.value })}
                                        onBlur={e => this.setState({ orgIdFilter: e.target.value })}
                                        margin="normal"
                                    />
                                </TableCell>
                                <TableCell align="left">
                                    <TextField id="filter-message"
                                        label="Message Contains"
                                        className={classes.textField}
                                        value={this.state.messageFilter}
                                        onChange={e => this.setState({ messageFilter: e.target.value })}
                                        onBlur={e => this.setState({ messageFilter: e.target.value })}
                                        margin="normal"
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Paper>
                <Fade in={!this.state.isLoading}><div>
                    <SharedMaterialReactTable
                        data={this.state.data}
                        columns={this.getLogsTableColumns()}
                        title="Logs"
                        isLoading={this.state.isLoading}
                        enablePagination={true}
                        enableSorting={true}
                        enableFiltering={true}
                        enableColumnActions={true}
                        enableRowActions={false}
                        onRowClick={(row) => this.onRowClick({ data: row })}
                        initialState={{
                            pagination: { pageIndex: 0, pageSize: this.state.rows },
                        }}
                        muiPaginationProps={{
                            rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
                        }}
                        emptyMessage="No logs found."
                    />
                </div></Fade>
                <Dialog open={this.state.openLogDialog} style={{ minHeight: '250' }}>
                    <DialogTitle id="max-width-dialog-title">
                        Log details
                        <div style={{ float: 'right' }}>
                            <span className={classes.copyIcon}
                                onClick={this.getLogDetails.bind(this, 'previous')}>
                                <ArrowBackIosIcon className={classes.arrow} />
                            </span>
                            &nbsp;
                            <span className={classes.copyIcon}
                                onClick={this.getLogDetails.bind(this, 'next')}>
                                <ArrowForwardIosIcon className={classes.arrow} />
                            </span>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <Typography className={classes.primaryBlue}>
                                            {this.state.selectedLogDate}
                                        </Typography>
                                    </td>
                                </tr>
                                <tr><td>&nbsp;</td></tr>
                                <tr>
                                    <td>
                                        <Typography className={classes.primaryBlue}>
                                            Message
                                        </Typography>
                                    </td>
                                </tr>
                                <tr style={{ paddingBottom: '10px' }}>
                                    <td>
                                        <TextareaAutosize
                                            name="validationText"
                                            className={classes.fullWidth}
                                            id="multiline-filled-static"
                                            disabled={true}
                                            maxRows={25}
                                            value={this.state.selectedLogMessage}
                                            style={{ width: '100%', minWidth: '300px' }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <Typography className={classes.primaryBlue}>
                                            Exception <span className={classes.copyIcon}
                                                onClick={this.handleCopyException.bind(this)}>
                                                <FileCopyIcon />
                                            </span>
                                        </Typography>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <TextareaAutosize
                                            name="validationText"
                                            className={classes.fullWidth}
                                            id="multiline-filled-static"
                                            disabled={true}
                                            maxRows={25}
                                            value={this.state.selectedLogException}
                                            style={{ width: '100%', minWidth: '300px' }}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            className={classes.disabledButton}
                            onClick={this.handleLogDialogClose.bind(this)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }

    // Define table columns for logs
    private getLogsTableColumns = (): ISharedTableColumn<IdssLog>[] => [
        {
            accessorKey: 'timeStamp',
            header: 'TimeStamp',
            enableSorting: true,
            enableFiltering: true,
        },
        {
            accessorKey: 'message',
            header: 'Message',
            enableSorting: true,
            enableFiltering: true,
        },
        {
            accessorKey: 'eventName',
            header: 'EventName',
            enableSorting: true,
            enableFiltering: true,
        },
        {
            accessorKey: 'submissionId',
            header: 'SubmissionId',
            enableSorting: true,
            enableFiltering: true,
        },
        {
            accessorKey: 'organizationId',
            header: 'OrganizationId',
            enableSorting: true,
            enableFiltering: true,
        },
        {
            accessorKey: 'userName',
            header: 'UserName',
            enableSorting: true,
            enableFiltering: true,
        },
        {
            accessorKey: 'level',
            header: 'Level',
            enableSorting: true,
            enableFiltering: true,
        },
    ];

    private selectedLevelChange(newValue) {
        this.setState({ logLevelFilter: newValue });
    }

    private handleFromDateChange(date) {
        var weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (!isNaN(Date.parse(date)) || date === null || date === "") {
            this.setState({ fromDateFilter: date ? date.toISOString() : weekAgo.toLocaleString() });
        } else {
            this.setState({ fromDateFilter: date });
        }
    }

    private handleToDateChange(date) {
        if (!isNaN(Date.parse(date)) || date === null || date === "") {
            this.setState({ toDateFilter: date ? date.toISOString() : (new Date().toLocaleString()) });
        } else {
            this.setState({ toDateFilter: date });
        }
    }

    private handleLogDialogClose = () => {
        this.setState({
            openLogDialog: false
        });
    }

    private handleCopyException = () => {
        navigator.clipboard.writeText(this.state.selectedLogException)
    }

    private refreshModel = async () => {
        this.setState({ isLoading: true });

        const page = Math.floor(this.state.first / this.state.rows) + 1;

        const countCall = DbService.GetLogCount$(this.props.user, this.state.subIdFilter.trim(), this.state.userNameFilter.trim(),
            this.state.orgIdFilter, (this.state.logLevelFilter === "" || this.state.logLevelFilter.value === "All") ? "" : this.state.logLevelFilter.value, this.state.messageFilter, this.state.fromDateFilter, this.state.toDateFilter);

        const countLogs = await firstValueFrom(countCall);

        if (countLogs instanceof AjaxError) {
            this.setRedirectState();
        }
        else if (countLogs && countLogs > 0) {
            this.setState({ totalRecords: countLogs });

            const apiCall = DbService.GetLogs$(this.props.user, this.state.subIdFilter.trim(), this.state.userNameFilter.trim(),
                this.state.orgIdFilter, (this.state.logLevelFilter === "" || this.state.logLevelFilter.value === "All") ? "" : this.state.logLevelFilter.value, this.state.messageFilter,
                this.state.fromDateFilter, this.state.toDateFilter,
                page, this.state.rows);

            const foundLogs: IdssLog[] | AjaxError = await firstValueFrom(apiCall);

            if (foundLogs instanceof AjaxError || !foundLogs) {
                this.setRedirectState();
            }
            else {
                this.setState({
                    data: foundLogs.map(
                        item => {
                            return {
                                id: item.logId,
                                timeStamp: moment(item.timeStamp).format("MM/DD/YYYY h:mm:ss a"),
                                message: item.message,
                                exception: item.exception,
                                eventName: item.eventName,
                                submissionId: item.submissionId,
                                organizationId: item.organizationId,
                                userName: item.userName,
                                level: item.level
                            };
                        }
                    )
                });
            }
        }
        else {
            this.setState({ data: [], totalRecords: 0 });
        }
        this.setState({ isLoading: false });
    }
}

// REDUX CONTAINER
const LogsViewWithState = connect<IGenericUserProps>(genericMapStateToProps)(LogsView);

// STYLES CONTAINER
const LogsViewPage = withStyles(styles)(LogsViewWithState);

export default LogsViewPage;
